import { computed } from 'vue';
import { useDocumentProperties } from '@/composables/useDocumentProperties';

export enum ScreenSizes {
  Phone = 'phone',
  SmallTablet = 'smallTablet',
  Tablet = 'tablet',
  ExtraSmallDesktop = 'extraSmallDesktop',
  SmallDesktop = 'smallDesktop',
  MediumDesktop = 'mediumDesktop',
  LargeDesktop = 'largeDesktop',
  ExtraLargeDesktop = 'extraLargeDesktop'
};

const SCREEN_SIZE_CLASSES = {
  [ScreenSizes.Phone]: 'screen-size-phone',
  [ScreenSizes.SmallTablet]: 'screen-size-small-tablet',
  [ScreenSizes.Tablet]: 'screen-size-tablet',
  [ScreenSizes.ExtraSmallDesktop]: 'screen-size-extra-small-desktop',
  [ScreenSizes.SmallDesktop]: 'screen-size-small-desktop',
  [ScreenSizes.MediumDesktop]: 'screen-size-medium-desktop',
  [ScreenSizes.LargeDesktop]: 'screen-size-large-desktop',
  [ScreenSizes.ExtraLargeDesktop]: 'screen-size-extra-large-desktop'
} as const;

type ScreenSizeClass = typeof SCREEN_SIZE_CLASSES[keyof typeof SCREEN_SIZE_CLASSES];

const PHONE_SIZE_THRESHOLD = 480;
const SMALL_TABLET_SIZE_THRESHOLD = 640;
const TABLET_SIZE_THRESHOLD = 767;
const EXTRA_SMALL_DESKTOP_SIZE_THRESHOLD = 890;
const SMALL_DESKTOP_SIZE_THRESHOLD = 1024;
const MEDIUM_DESKTOP_SIZE_THRESHOLD = 1105;
const LARGE_DESKTOP_SIZE_THRESHOLD = 1240;

const screenType = computed<ScreenSizes>(() => {
  const { clientWidth } = useDocumentProperties();

  if (clientWidth.value <= PHONE_SIZE_THRESHOLD) {
    return ScreenSizes.Phone;
  }

  if (clientWidth.value <= SMALL_TABLET_SIZE_THRESHOLD) {
    return ScreenSizes.SmallTablet;
  }

  if (clientWidth.value <= TABLET_SIZE_THRESHOLD) {
    return ScreenSizes.Tablet;
  }

  if (clientWidth.value <= EXTRA_SMALL_DESKTOP_SIZE_THRESHOLD) {
    return ScreenSizes.ExtraSmallDesktop;
  }

  if (clientWidth.value <= SMALL_DESKTOP_SIZE_THRESHOLD) {
    return ScreenSizes.SmallDesktop;
  }

  if (clientWidth.value <= MEDIUM_DESKTOP_SIZE_THRESHOLD) {
    return ScreenSizes.MediumDesktop;
  }

  if (clientWidth.value <= LARGE_DESKTOP_SIZE_THRESHOLD) {
    return ScreenSizes.LargeDesktop;
  }

  return ScreenSizes.ExtraLargeDesktop;
});

const screenTypeClass = computed<ScreenSizeClass>(() => {
  return SCREEN_SIZE_CLASSES[screenType.value];
});

const isMobile = computed<boolean>(() => {
  const { clientWidth } = useDocumentProperties();

  return clientWidth.value <= TABLET_SIZE_THRESHOLD;
});

const shouldShowFreestarAdsOnRightSideOfChart = computed<boolean>(() => {
  const { clientWidth } = useDocumentProperties();

  return clientWidth.value > LARGE_DESKTOP_SIZE_THRESHOLD;
});

const shouldShowFreestarAdsAboveChart = computed<boolean>(() => {
  return !shouldShowFreestarAdsOnRightSideOfChart.value;
});

const shouldShowInteractiveTour = computed(() => {
  const { clientWidth } = useDocumentProperties();

  return clientWidth.value > TABLET_SIZE_THRESHOLD;
});

const shouldTurnAutoSizeChartsOnByDefault = computed(() => {
  const { clientWidth } = useDocumentProperties();

  return clientWidth.value <= SMALL_DESKTOP_SIZE_THRESHOLD;
});

const shouldShowAutoSizeChartsCheckboxInControlBarUpper = computed(() => {
  const { clientWidth } = useDocumentProperties();

  return clientWidth.value <= SMALL_DESKTOP_SIZE_THRESHOLD;
});

export function useScreenSizing() {
  return {
    screenTypeClass,
    isMobile,
    shouldShowFreestarAdsOnRightSideOfChart,
    shouldShowFreestarAdsAboveChart,
    shouldShowInteractiveTour,
    shouldTurnAutoSizeChartsOnByDefault,
    shouldShowAutoSizeChartsCheckboxInControlBarUpper
  };
}
